import React, { useState, useEffect } from "react";

import {
    Box,
    Divider,
    TextField,
    Grid,
    Switch,
    FormControlLabel,
    styled,
    Card,
    Select,
    MenuItem,
    FormControl,
    FormHelperText,
    InputLabel,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Chip,
    List,
    ListItem,
} from '@mui/material';

import { LoadingButton } from '@mui/lab';
import { useNavigate } from "react-router-dom"
import { useFormik } from 'formik';

import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonRestricted from '../../common/CommonRestricted';
import { useAppContextFeatures } from '../../../context/AppContext';

import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { mergeSchema } from "../../../utils/dataSchema"
import { spliceString } from "../../../utils/formatString";

import { useUsers, userTypes, targetTypes } from "../../../hooks/api/useUsers";
import { useCustomFields, customFieldModules, customFieldFunctions } from '../../../hooks/api/useCustomFields';
import { useTranslations } from '../../../hooks/useTranslations';

import { useLocations, locationTypes, targetTypes as locationTargetTypes } from '../../../hooks/api/useLocations';
import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import {
    initialValues,
    initialPasswordValues,
    validationSchema,
    validationPasswordSchema
} from "../../../schema/users/driverSchema";
import { inputTypes, inputTypesDef } from "../../../schema/customFields/customFieldSchema"

import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useStateCustomFields } from "../../../hooks/state/useStateCustomFields";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

export default function NewDriver({
    routeType
}) {

    const { t } = useTranslations({ ns: ["common", "@user"] })

    const { promptMessage } = useSystemPrompt();
    const { setSystemLoading } = useAppContextFeatures()

    const { isLoading, create, filterOperatorTypes } = useUsers({ userType: userTypes.driver, targetType: targetTypes.driver });
    const { list: listCustomFields } = useCustomFields({ customFieldModule: customFieldModules.user, customFieldFunction: customFieldFunctions.user.driver })

    const { list: listLocation } = useLocations({ locationType: locationTypes.location, targetType: targetTypes.location });
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.user.id, scopeTypes.user.driver.id)

    const {
        dataCustomFields,

        getInitialFormStateRows,
        getSubmitFormDataFromRows,

        getCustomFieldOptionNameByIds,

        RenderCustomFieldSelectionTextbox,
        RenderCustomFieldSelectionDropdown,
        RenderCustomFieldSelectionMultiDropdown
    } = useStateCustomFields(
        {
            fieldModule: customFieldModules.user,
            moduleFunction: customFieldFunctions.user.driver
        }
    )

    const navigate = useNavigate();
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)
    const [dataLocations, setDataLocations] = useState([])
    const [customFields, setCustomFields] = useState([])

    const validationSchemaCombine = mergeSchema(validationSchema, validationPasswordSchema);

    const initialValuesCombine = {
        ...initialValues,
        ...initialPasswordValues
    }

    const onSubmit = async (currentValues) => {

        try {

            const { rows } = values;

            let cf = getSubmitFormDataFromRows({ rows })

            let data = {
                credential: {
                    username: currentValues?.username,
                    password: currentValues?.password,
                    firstname: currentValues?.firstname,
                    lastname: currentValues?.lastname,
                    contact: currentValues?.contactno,
                    email: currentValues?.email,
                    status: currentValues?.status ? 1 : 0,
                },
                driver: {
                    company_name: currentValues?.companyName,
                    remark: currentValues?.remark
                },
                props: {
                    location_id: currentValues?.locationId
                },
                custom_fields: cf
            }

            if (cf.length) {
                data.custom_fields = cf
            }

            const response = await create({ data });

            promptMessage(true, `${response?.message}`)
            navigate(getRoute("users/drivers"))

        } catch (error) {
            promptMessage(false, `Action Failed. (${error?.message})`)
        }

    }

    const loadInitialCustomFieldState = async () => {

        const cf = await getInitialFormStateRows({
            savedCustomFields: []
        })

        setFieldValue(`rows`, cf)
    }

    const fetchLocationData = async () => {
        try {

            const props = {
                limit: -1,
                offset: 0,
                filterValue: [],
                sortInfo: {
                    id: "location_name",
                    dir: 0,
                }
            }

            props.locationType = locationTypes.location

            const { data } = await listLocation(props)

            setDataLocations(data?.locations)

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }
    }

    useEffect(() => {

        fetchLocationData()
        loadInitialCustomFieldState()

    }, [])

    const formik = useFormik({
        initialValues: initialValuesCombine,
        validationSchema: validationSchemaCombine,
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;

    const getLocationNameByIds = (optionIdArray) => {

        if (!optionIdArray) return []

        let optionNames = [];

        dataLocations.forEach((e) => {
            // e.options.forEach((e2) => {
            // if(e2.option_key === optionIdArray){
            if (optionIdArray.includes(e.id)) {
                optionNames.push(e.location_name)
            }
            // })
        })

        return optionNames
    }

    const RenderCustomFieldSelection = () => {

        if (dataCustomFields.length <= 0) return <></>

        if (!values?.rows) return <></>

        const rows = values.rows

        return (
            <>
                <Grid item xs={12}>
                    <Divider sx={{ mt: 2, mb: 1 }} />
                </Grid>

                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                    <CommonSectionHeader
                        title={t('customProperties')}
                        subTitle={t('provideCustomItemInformation')}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={9}>
                    <List>
                        {
                            rows.map((item, index) => {

                                let itemValues = item

                                let {
                                    fieldType,
                                    fieldLabel,
                                    fieldKey
                                } = item

                                return (
                                    <ListItem
                                        key={index}
                                        divider
                                        disablePadding
                                        sx={{
                                            py: 1
                                        }}
                                    >
                                        <Grid container spacing={1}>
                                            <Grid item md={3} xs={12} sm={12} display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"} >
                                                <Chip
                                                    label={fieldLabel}
                                                    color='primary'
                                                    variant='outlined'
                                                    size='large'
                                                    // avatar={<CommonIcons name={icons.add} />}
                                                    sx={{
                                                        width: '100%',
                                                        // textAlign: 'left',
                                                        fontSize: 16,
                                                        fontWeight: 800
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item md={9} xs={12} sm={12}>

                                                {
                                                    fieldType === inputTypesDef.textbox &&

                                                    RenderCustomFieldSelectionTextbox({
                                                        index,
                                                        item,
                                                        itemValues,
                                                        inputProps: {
                                                            fullWidth: true,
                                                            name: `rows.${index}.customValue`,
                                                            label: itemValues?.fieldLabel,
                                                            type: "text",
                                                            variant: "outlined",
                                                            value: itemValues?.customValue,
                                                            onChange: (event) => setFieldValue(`rows.${index}.customValue`, event.target.value),
                                                        }
                                                    })
                                                }

                                                {
                                                    fieldType === inputTypesDef.dropdown &&

                                                    RenderCustomFieldSelectionDropdown({
                                                        index,
                                                        item,
                                                        itemValues,
                                                        selectProps: {
                                                            labelId: "customValue",
                                                            label: itemValues?.fieldLabel,
                                                            name: `rows.${index}.customArray`,
                                                            onChange: (event) => {
                                                                setFieldValue(`rows.${index}.customArray`, [event.target.value])
                                                            },
                                                            size: 'small',
                                                            margin: 'dense',
                                                            value: itemValues.customArray.length ? itemValues.customArray[0] : ""
                                                        }
                                                    })
                                                }


                                                {
                                                    fieldType === inputTypesDef.multidropdown &&

                                                    RenderCustomFieldSelectionMultiDropdown({
                                                        index,
                                                        item,
                                                        itemValues,
                                                        selectProps: {
                                                            labelId: itemValues?.fieldLabel,
                                                            label: itemValues?.fieldLabel,
                                                            name: `rows.${index}.customArray`,
                                                            onChange: handleChange,
                                                            renderValue: (selected) => {

                                                                return (
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                                        {
                                                                            getCustomFieldOptionNameByIds(selected, dataCustomFields, fieldKey).map((i, index) => (
                                                                                <Chip
                                                                                    key={index}
                                                                                    // color={'red'}
                                                                                    label={spliceString({ val: i, maxLength: 20 })}
                                                                                />
                                                                            ))
                                                                        }
                                                                    </Box>
                                                                )

                                                            },
                                                            size: 'small',
                                                            margin: 'dense',
                                                            multiple: true,
                                                            input: <OutlinedInput id="select-multiple-chip" label="Chip" />,
                                                            value: itemValues?.customArray ? itemValues.customArray : []
                                                        }
                                                    })
                                                }

                                            </Grid>
                                        </Grid>
                                    </ListItem>
                                )

                            })
                        }

                    </List>

                </Grid>
            </>
        )
    }

    const RenderLocation = () => {

        return (
            <>
                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                    <CommonSectionHeader
                        title={t('locationPermissions')}
                        subTitle={t('dataAccessScopes')}
                    />
                </Grid>


                <Grid item xs={12} sm={12} md={9}>
                    <FormControl
                        margin="dense"
                        fullWidth
                        size="small"
                        error={touched.locationId && Boolean(errors.locationId)}
                    >

                        <InputLabel id="location">{t('location')}</InputLabel>
                        <Select
                            labelId="locationId"
                            label={t('location')}
                            name={"locationId"}
                            onChange={(event) => {
                                setFieldValue(`locationId`, event.target.value)
                            }}
                            value={values.locationId.length ? values.locationId : []}
                            renderValue={(selected) => {

                                return (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {
                                            getLocationNameByIds(selected).map((i, index) => (
                                                <Chip
                                                    key={index}
                                                    label={spliceString({ val: i, maxLength: 20 })}
                                                />
                                            ))
                                        }
                                    </Box>
                                )

                            }}
                            multiple
                            input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
                        >
                            {
                                dataLocations?.map((i, index) => {
                                    return (
                                        <MenuItem key={index} value={i.id}>
                                            <Checkbox checked={values.locationId.indexOf(i?.id) > -1} />
                                            <ListItemText
                                                primary={`${i.location_name}`}
                                            />
                                        </MenuItem>
                                    )
                                })
                            }
                        </Select>
                        <FormHelperText>
                            {touched.locationId && errors.locationId}
                        </FormHelperText>
                    </FormControl>
                </Grid>
            </>
        )

    }

    const RenderCredentialInfo = () => {
        return (
            <>
                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                    <CommonSectionHeader
                        title={t('credential')}
                        subTitle={t('accessAuthentication')}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={9}>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                autoFocus
                                fullWidth
                                name={"username"}
                                label={t('username')}
                                type="text"
                                variant="outlined"
                                value={values.username}
                                onChange={handleChange}
                                error={touched.username && Boolean(errors.username)}
                                helperText={touched.username && errors.username}
                            />
                        </Grid>
                        <Grid item xs={12} md={6} textAlign="left">
                            <FormControlLabel
                                control={
                                    <Switch
                                        sx={{
                                            ml: 2,
                                            my: 1
                                        }}
                                        name="status"
                                        checked={Boolean(values.status)}
                                        onChange={(event, checked) => {
                                            setFieldValue("status", checked);
                                        }}
                                    />
                                }
                                label={
                                    Boolean(values?.status) ? t('enabled') : t('disabled')
                                }
                            />
                        </Grid>

                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"password"}
                                label={t('password')}
                                type="password"
                                variant="outlined"
                                value={values.password}
                                onChange={handleChange}
                                error={touched.password && Boolean(errors.password)}
                                helperText={touched.password && errors.password}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"passwordconfirm"}
                                label={t('confirmPassword')}
                                type="password"
                                variant="outlined"
                                value={values.passwordconfirm}
                                onChange={handleChange}
                                error={touched.passwordconfirm && Boolean(errors.passwordconfirm)}
                                helperText={touched.passwordconfirm && errors.passwordconfirm}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </>
        )
    }

    const RenderProfileInfo = () => {
        return (
            <>
                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                    <CommonSectionHeader
                        title={t('driverProfile')}
                        subTitle={t('fullDetailsOfDriverInformation')}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={9}>
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"firstname"}
                                label={t('firstName')}
                                type="text"
                                variant="outlined"
                                value={values.firstname}
                                onChange={handleChange}
                                error={touched.firstname && Boolean(errors.firstname)}
                                helperText={touched.firstname && errors.firstname}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"lastname"}
                                label={t('lastName')}
                                type="text"
                                variant="outlined"
                                value={values.lastname}
                                onChange={handleChange}
                                error={touched.lastname && Boolean(errors.lastname)}
                                helperText={touched.lastname && errors.lastname}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"companyName"}
                                label={t('companyName')}
                                type="text"
                                variant="outlined"
                                value={values.companyName}
                                onChange={handleChange}
                                error={touched.companyName && Boolean(errors.companyName)}
                                helperText={touched.companyName && errors.companyName}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"email"}
                                label={t('email')}
                                type="email"
                                variant="outlined"
                                value={values.email}
                                onChange={handleChange}
                                error={touched.email && Boolean(errors.email)}
                                helperText={touched.email && errors.email}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"contactno"}
                                label={t('contact')}
                                type="text"
                                variant="outlined"
                                value={values.contactno}
                                onChange={handleChange}
                                error={touched.contactno && Boolean(errors.contactno)}
                                helperText={touched.contactno && errors.contactno}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                rows={3}
                                multiline
                                name={"remark"}
                                label={t('remark')}
                                type="text"
                                variant="outlined"
                                value={values.remark}
                                onChange={handleChange}
                                error={touched.remark && Boolean(errors.remark)}
                                helperText={touched.remark && errors.remark}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </>
        )
    }

    const RenderForm = () => {

        return (

            <form onSubmit={formik.handleSubmit}>
                <StyledCard>
                    <Grid container spacing={1}>

                        {
                            RenderCredentialInfo()
                        }

                        <Grid item xs={12}>
                            <Divider sx={{ mt: 1, mb: 1 }} />
                        </Grid>

                        {
                            RenderProfileInfo()
                        }

                        <Grid item xs={12}>
                            <Divider sx={{ mt: 1, mb: 1 }} />
                        </Grid>

                        {
                            RenderLocation()
                        }

                        {
                            RenderCustomFieldSelection()
                        }

                    </Grid>
                </StyledCard>
                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"right"}>
                        <LoadingButton
                            type="submit"
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            loading={isLoading}
                        >
                            {t('saveChanges')}
                        </LoadingButton>
                    </Grid>
                </Grid>
            </form>

        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.newDriver })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t('newDriver')}
                breadcrumbs={[
                    {
                        displayText: t('drivers'),
                        props: {
                            href: getRoute("users/drivers")
                        }
                    },
                    {
                        displayText: t('newDriver'),
                        props: {
                            color: 'secondary.main'
                        }
                    },
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageNew.id }) ?
                    RenderForm()
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}